import * as Sentry from '@sentry/react';

import { REPLAY_BLOCK_SELECTOR, scrubLawLibraryBreadcrumb, scrubLawLibraryEvent, scrubReplayRecordingEvent } from './lib/lawLibrarySentryScrub';
import { installReplayRouteGuard } from './lib/replayPause';

/** Initialize the singleton browser client before tagging any app surface. */
export function initializeSentry() {
  if (Sentry.getClient()) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: false,
        // The Law Library's links carry its query in their href, which masking does not touch (#1964).
        block: [REPLAY_BLOCK_SELECTOR],
        // Replay's own navigation entries are custom recording events that name the page URL.
        beforeAddRecordingEvent: (event) => scrubReplayRecordingEvent(event),
      }),
    ],
    tracesSampleRate: 0.01,
    sampleRate: 1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
    // Spec 7.5 (#1964): the Law Library keeps its query in its URL; no event carries it.
    beforeSend: (event) => scrubLawLibraryEvent(event),
    beforeSendTransaction: (event) => scrubLawLibraryEvent(event),
    beforeBreadcrumb: (breadcrumb) => scrubLawLibraryBreadcrumb(breadcrumb),
    ignoreErrors: [
      // #VALENTINE-11T: PDF.js worker transport teardown synchronously throws if getPage
      // or other operations race document destruction during unmount or page transitions.
      /sendWithPromise/,
      /Transport destroyed/,
      // #VALENTINE-11S: Corrupted, malformed, or non-PDF files uploaded by clients/third parties
      // are document validation errors, not actionable frontend application crashes.
      /InvalidPDFException/,
      /Invalid PDF structure/,
      // #VALENTINE-ZB, #VALENTINE-Z4, #VALENTINE-10V, #VALENTINE-10T: MSAL silent auth failures
      // (network drops, background tab throttling timeouts, and natural session/refresh token expiry)
      // are ordinary auth lifecycle events handled cleanly by the 401 interceptor, not frontend application crashes.
      /post_request_failed/,
      /timed_out/,
      /interaction_required/,
      // #VALENTINE-118, #VALENTINE-119, #VALENTINE-11Y, #VALENTINE-11A, #VALENTINE-ZE:
      // Transient client timeout and network drop noise (e.g. laptop sleep, background tab throttling,
      // offline/Wi-Fi drops) are client environment conditions, not frontend application crashes.
      /timeout exceeded/,
      /timeout of \d+ms exceeded/,
      /Network Error/,
      // #VALENTINE-10W: Missing or expired certified mail preview/download artifacts (404) are
      // handled expected states in the UI, not application crashes.
      /Failed to (?:load|download) mailing artifact \(404\)/,
      /Failed to download proof package \(404\)/,
    ],
  });
  // Replay events do not pass beforeSend; event processors do run on them (their `urls`).
  Sentry.addEventProcessor((event) => scrubLawLibraryEvent(event));
  // Replay stops before a /law-library URL is set, not after the route renders (#1964 Plan 5).
  // No window in a node test of this module (productionSentryConfig.test.ts).
  if (typeof window !== 'undefined') installReplayRouteGuard(window);
}
